import React, { useState, useRef, useCallback, useEffect } from "react";
import { Card, CardContent, Typography, Chip, CircularProgress, Box, Stack, Tooltip, InputBase, IconButton, useTheme, useMediaQuery } from "@mui/material";
import { Search, X, Star, Wifi, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { getCourses } from "../../api/courses";
import api from "../../api/axios";
import { fadeUp } from "../../utils/animations";

const CATEGORIES = ["CLOUD", "SECURITY", "NETWORKING", "DEVELOPMENT", "DEVOPS", "DATA", "MANAGEMENT"];

/* ------------------------------------------------------------------ */
/*  card_image_url comes back as a relative path like                  */
/*  "/media/courses/cards/xyz.png". Rendered directly, that resolves   */
/*  against the React dev server's own origin (not the API server),    */
/*  so it 404s and the <img> never loads. Resolve it against the       */
/*  API's actual origin instead — same pattern as FeaturedCourses.     */
/* ------------------------------------------------------------------ */
const API_ORIGIN = new URL(api.defaults.baseURL, window.location.origin).origin;

function resolveMediaUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path; // already absolute
  return `${API_ORIGIN}${path.startsWith("/") ? "" : "/"}${path}`;
}

// Label → gradient + emoji, used only when there's no image at all
const LABEL_META = {
  CLOUD:    { gradient: "linear-gradient(135deg, #EFF6FF, #DBEAFE)", emoji: "☁️" },
  AZURE:    { gradient: "linear-gradient(135deg, #EFF6FF, #BFDBFE)", emoji: "🔷" },
  SECURITY: { gradient: "linear-gradient(135deg, #FFF1F2, #FFE4E6)", emoji: "🔒" },
  DEVOPS:   { gradient: "linear-gradient(135deg, #F0FDF4, #DCFCE7)", emoji: "⚙️" },
  DEFAULT:  { gradient: "linear-gradient(135deg, #F5F3FF, #EDE9FE)", emoji: "📘" },
};

const LEVEL_COLOR = {
  BEGINNER:     { bg: "rgba(5,150,105,0.1)", fg: "#059669" },
  INTERMEDIATE: { bg: "rgba(217,119,6,0.1)", fg: "#D97706" },
  PROFESSIONAL: { bg: "rgba(220,38,38,0.1)", fg: "#DC2626" },
  ADVANCED:     { bg: "rgba(220,38,38,0.1)", fg: "#DC2626" },
};

const CARD_HEIGHT = 420;
const THUMB_HEIGHT = 170;
const CARD_GAP = 24; // px, keep in sync with the gap set on the track below

function formatPrice(price, discountPrice) {
  const fmt = (v) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(v);
  if (discountPrice) {
    return (
      <Stack direction="row" spacing={1} alignItems="baseline">
        <Typography fontWeight={800} fontSize={16} color="#0B1530">{fmt(discountPrice)}</Typography>
        <Typography fontSize={12.5} color="text.disabled" sx={{ textDecoration: "line-through" }}>{fmt(price)}</Typography>
      </Stack>
    );
  }
  return <Typography fontWeight={800} fontSize={16} color="#0B1530">{fmt(price)}</Typography>;
}

function StarRating({ rating }) {
  const val = parseFloat(rating) || 0;
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Star size={13} fill={val > 0 ? "#F59E0B" : "none"} color={val > 0 ? "#F59E0B" : "#CBD5E1"} />
      <Typography fontSize={12} fontWeight={600} color={val > 0 ? "#334155" : "#94A3B8"}>
        {val > 0 ? val.toFixed(1) : "No ratings"}
      </Typography>
    </Stack>
  );
}

// Thumbnail that falls back to a gradient + emoji tile if there's no
// image, or if the resolved URL 404s / fails to load.
function CourseThumb({ src, alt, label }) {
  const [failed, setFailed] = useState(!src);
  const meta = LABEL_META[label] ?? LABEL_META.DEFAULT;

  if (failed) {
    return (
      <Box
        sx={{
          height: THUMB_HEIGHT, flexShrink: 0, background: meta.gradient,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        <Typography fontSize={44}>{meta.emoji}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: THUMB_HEIGHT, flexShrink: 0, overflow: "hidden", bgcolor: "#F1F5FB" }}>
      <Box
        component="img"
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        className="course-thumb-img"
        sx={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.5s ease" }}
      />
    </Box>
  );
}

// A single course card. Unchanged from the grid version — just no longer
// owns its own width (that's set by the motion.div slot in the track).
function CourseCard({ c }) {
  return (
    <Card
      component={c.slug ? Link : "div"}
      to={c.slug ? `/courses/${c.slug}` : undefined}
      className="course-carousel-card"
      sx={{
        height: CARD_HEIGHT,
        width: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        textDecoration: "none",
        color: "inherit",
        border: "1px solid rgba(15,23,42,0.09)",
        borderRadius: "12px",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
        cursor: c.slug ? "pointer" : "default",
        scrollSnapAlign: "start",
        transition: "transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: "0 16px 32px -20px rgba(15,23,42,0.22)",
          borderColor: "rgba(15,23,42,0.13)",
        },
        "&:hover .course-thumb-img": { transform: "scale(1.04)" },
        "&:hover .course-title": { color: "#2563EB" },
      }}
    >
      <CourseThumb src={resolveMediaUrl(c.card_image_url)} alt={c.title} label={c.label} />

      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          gap: 1,
          minHeight: 0,
          p: 2.5,
          "&:last-child": { pb: 2.5 },
        }}
      >
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          {c.label && (
            <Chip
              label={c.label}
              size="small"
              sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB" }}
            />
          )}
          {c.level && (
            <Chip
              label={c.level}
              size="small"
              sx={{
                height: 20, fontSize: 11, fontWeight: 700,
                bgcolor: (LEVEL_COLOR[c.level] ?? { bg: "rgba(15,23,42,0.05)" }).bg,
                color: (LEVEL_COLOR[c.level] ?? { fg: "#475569" }).fg,
              }}
            />
          )}
        </Stack>

        <Tooltip title={c.title} arrow placement="top" enterDelay={400}>
          <Typography
            className="course-title"
            sx={{
              fontWeight: 700, fontSize: "1.02rem", color: "#0B1530", lineHeight: 1.35,
              transition: "color 0.2s ease",
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
              overflow: "hidden", minHeight: "calc(1.35em * 2)",
            }}
          >
            {c.title}
          </Typography>
        </Tooltip>

        {c.short_description && (
          <Typography
            sx={{
              fontSize: 13, color: "#64748B", lineHeight: 1.6,
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
              overflow: "hidden", minHeight: "calc(1.6em * 2)",
            }}
          >
            {c.short_description}
          </Typography>
        )}

        <Box flexGrow={1} />

        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <StarRating rating={c.rating} />
          {c.mode && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Wifi size={13} color="#64748B" />
              <Typography fontSize={12} color="#64748B" fontWeight={600}>{c.mode}</Typography>
            </Stack>
          )}
        </Stack>

        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
          {c.price != null ? formatPrice(c.price, c.discount_price) : <Box />}
          <ArrowRight size={16} color="#334155" strokeWidth={2} />
        </Stack>
      </CardContent>
    </Card>
  );
}

export default function CourseGrid({ initialSearch = "", initialCategory = "" }) {
  const [search, setSearch] = useState(initialSearch);
  const [category, setCategory] = useState(initialCategory);
  const [focused, setFocused] = useState(false);

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ["courses", { search, category }],
    queryFn: () => getCourses({
      ...(search && { search }),
      ...(category && { category }),
    }).then(r => r.data),
  });

  // ---- Carousel state/logic, same pattern as TrainingSection ----
  const trackRef = useRef(null);
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMd = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const cardsPerView = isXs ? 1 : isSm ? 2 : isMd ? 3 : 4;

  const [activePage, setActivePage] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  // Width so exactly `cardsPerView` cards + gaps fill the track, with no
  // partial card poking past the edge. Applied ONLY on the motion.div
  // (direct flex child of the track) — never inside CourseCard itself.
  const cardWidthCss = `calc((100% - ${CARD_GAP * (cardsPerView - 1)}px) / ${cardsPerView})`;

  const totalPages = Math.max(1, Math.ceil(courses.length / cardsPerView));

  useEffect(() => {
    trackRef.current?.scrollTo({ left: 0 });
    setActivePage(0);
  }, [cardsPerView, search, category]);

  const scrollByPage = useCallback((dir) => {
    const track = trackRef.current;
    if (!track) return;
    const firstCard = track.querySelector(".course-carousel-card");
    const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : track.clientWidth / cardsPerView;
    const step = (cardWidth + CARD_GAP) * cardsPerView;
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  }, [cardsPerView]);

  const handleTrackScroll = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    setAtStart(track.scrollLeft <= 2);
    setAtEnd(track.scrollLeft >= max - 2);
    if (track.clientWidth) {
      const page = Math.round(track.scrollLeft / track.clientWidth);
      setActivePage(Math.max(0, Math.min(page, totalPages - 1)));
    }
  }, [totalPages]);

  return (
    <Box component="section" sx={{ position: "relative", bgcolor: "#FAFBFF", py: { xs: 7, md: 9 }, overflow: "hidden" }}>
      {/* Blueprint grid backdrop, consistent with other sections */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(29,78,216,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(29,78,216,0.04) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse 70% 50% at 50% 0%, black 0%, transparent 78%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 50% at 50% 0%, black 0%, transparent 78%)",
        }}
      />

      <Box sx={{ maxWidth: 1280, mx: "auto", px: { xs: 2, md: 4 }, position: "relative" }}>
        {/* Search + filter panel */}
        <Box
          sx={{
            borderRadius: "16px",
            border: "1px solid rgba(15,23,42,0.07)",
            bgcolor: "white",
            boxShadow: "0 1px 3px rgba(15,23,42,0.05)",
            p: { xs: 2, sm: 2.75 },
            mb: 4.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              bgcolor: "#F8FAFF",
              borderRadius: "11px",
              border: "1px solid",
              borderColor: focused ? "#2563EB" : "rgba(15,23,42,0.08)",
              boxShadow: focused ? "0 0 0 4px rgba(37,99,235,0.10)" : "none",
              px: 2,
              py: 1.2,
              mb: 2,
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
            }}
          >
            <Search size={18} color={focused ? "#2563EB" : "#94A3B8"} style={{ flexShrink: 0 }} />
            <InputBase
              fullWidth
              placeholder="Search courses, skills, certifications"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              sx={{ fontSize: 14.5, fontWeight: 500, "& input::placeholder": { color: "#94A3B8", opacity: 1 } }}
            />
            {search.length > 0 && (
              <Box
                role="button"
                aria-label="Clear search"
                onClick={() => setSearch("")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  color: "#94A3B8",
                  cursor: "pointer",
                  flexShrink: 0,
                  "&:hover": { bgcolor: "rgba(15,23,42,0.06)", color: "#475569" },
                }}
              >
                <X size={13} />
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 0.85, flexWrap: "wrap" }}>
            {["", ...CATEGORIES].map((cat) => {
              const isActive = category === cat;
              return (
                <Box
                  key={cat || "all"}
                  component="button"
                  onClick={() => setCategory(cat === category ? "" : cat)}
                  sx={{
                    appearance: "none",
                    border: "1px solid",
                    borderColor: isActive ? "#2563EB" : "rgba(15,23,42,0.10)",
                    bgcolor: isActive ? "#2563EB" : "white",
                    color: isActive ? "white" : "#334155",
                    fontSize: 12.5,
                    fontWeight: 600,
                    borderRadius: 999,
                    px: 1.6,
                    py: 0.6,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all 0.15s ease",
                    "&:hover": isActive
                      ? { bgcolor: "#1D4ED8" }
                      : { borderColor: "#2563EB", color: "#1D4ED8", bgcolor: "rgba(37,99,235,0.04)" },
                  }}
                >
                  {cat || "All"}
                </Box>
              );
            })}
          </Box>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress size={26} thickness={4} sx={{ color: "#2563EB" }} />
          </Box>
        ) : courses.length === 0 ? (
          <Box textAlign="center" py={8}>
            <Typography fontWeight={600} color="#334155">No courses found</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Try a different search term or category.
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              ref={trackRef}
              onScroll={handleTrackScroll}
              className="course-carousel-track"
              sx={{
                display: "flex",
                gap: `${CARD_GAP}px`,
                overflowX: "auto",
                overflowY: "hidden",
                scrollSnapType: "x mandatory",
                scrollBehavior: "smooth",
                pb: 1.5,
                px: { xs: 0.5, md: 0 },
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {courses.map((c, i) => (
                <motion.div
                  key={c.id}
                  {...fadeUp(i * 0.03)}
                  style={{
                    display: "block",
                    flex: `0 0 ${cardWidthCss}`,
                    width: cardWidthCss,
                  }}
                >
                  <CourseCard c={c} />
                </motion.div>
              ))}
            </Box>

            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 3 }}>
              <IconButton
                onClick={() => scrollByPage(-1)}
                disabled={atStart}
                sx={{
                  width: 40, height: 40,
                  border: "1px solid rgba(37,99,235,0.25)",
                  color: "#2563EB",
                  bgcolor: "white",
                  "&:hover": { bgcolor: "#EFF6FF" },
                  "&.Mui-disabled": { opacity: 0.35 },
                }}
              >
                <ChevronLeft size={18} />
              </IconButton>

              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0B1530", minWidth: 64, textAlign: "center" }}>
                {String(activePage + 1).padStart(2, "0")} / {String(totalPages).padStart(2, "0")}
              </Typography>

              <IconButton
                onClick={() => scrollByPage(1)}
                disabled={atEnd}
                sx={{
                  width: 40, height: 40,
                  border: "1px solid rgba(37,99,235,0.25)",
                  color: "#2563EB",
                  bgcolor: "white",
                  "&:hover": { bgcolor: "#EFF6FF" },
                  "&.Mui-disabled": { opacity: 0.35 },
                }}
              >
                <ChevronRight size={18} />
              </IconButton>
            </Stack>
          </>
        )}
      </Box>
    </Box>
  );
}